import { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCartItems,
  selectCartRestaurantDetails,
  selectCateringItems,
  selectCateringCartPackages,
  selectCateringCartItems,
  selectCateringCartFilters,
  selectIsModifyOrderMode,
  selectModifyOrderPax,
  selectModifyOrderOriginalTotal,
  selectCateringAdditionalServiceFilters,
  selectCateringBrandsList,
  selectCateringRestaurantId,
} from "../../redux/features/restaurants/restaurantSelectors";
import {
  setCartItems,
  setCartRestaurantDetails,
  setCartSubTotal,
  setShowProductDetail,
  addVariantItem,
  removeVariantItem,
  setEditingVariantItem,
  setCateringCartItems,
  setCateringCartSubtotal,
  addCateringCartVariantItem,
  removeCateringCartVariantItem,
  addItemToCateringPackage,
  removeItemFromCateringPackage,
  addVariantItemToCateringPackage,
  removeVariantItemFromCateringPackage,
} from "../../redux/features/restaurants/restaurantSlice";
import { updateSubtotal } from "../../utils/menu";
import VariantDropdown from "./variantDropdown";
import AddOnDropdown from "./AddOnDropdown";
import MenuItemCard from "./menuItemCard";
import ItemDescriptionTooltip from "./ItemDescriptionTooltip";
import { getItemDetailsAsync } from "../../redux/features/restaurants/restaurantThunks";
import { getCategoryLimitsForPax } from "../../utils/paxCalculations";
import {
  calculateItemDisplayPriceInPackage,
  calculateTotalChargeableItemsCost,
  calculateTotalPackageBasePrice,
  calculateVariantItemPrice,
  getCateringItemPrice,
  itemRequiresAddOnSelection,
  shouldShowAddOnOptions
} from "../../utils/catering";
import AddOnSelectionModal from "./AddOnSelectionModal";
import RemoveItemWarningModal from "./RemoveItemWarningModal";

const CateringMenuList = ({ activeCategory, searchQuery, activePackageId = null, disabled = false, isModifyOrderMode = false, isAdditionalService = false, additionalServiceItems = null }) => {
  // Get modify order mode from Redux
  const reduxIsModifyOrderMode = useSelector(selectIsModifyOrderMode);
  const reduxModifyOrderPax = useSelector(selectModifyOrderPax);
  const reduxModifyOrderOriginalTotal = useSelector(selectModifyOrderOriginalTotal);

  // Only apply modify order mode if explicitly passed OR Redux state confirms it
  const actualModifyMode = (isModifyOrderMode || reduxIsModifyOrderMode) &&
    reduxModifyOrderPax !== null;

  console.log('CateringMenuList actualModifyMode:', {
    actualModifyMode,
    isModifyOrderMode,
    reduxIsModifyOrderMode,
    reduxModifyOrderPax,
    reduxModifyOrderOriginalTotal,
    isAdditionalService
  });
  
  // Use additional service items if this is an additional service menu
  const cateringItemsListFromRedux = useSelector(selectCateringItems);
  const cateringItemsList = isAdditionalService && additionalServiceItems 
    ? additionalServiceItems 
    : cateringItemsListFromRedux;
  
  console.log('🔍 CateringMenuList - isAdditionalService:', isAdditionalService);
  console.log('🔍 CateringMenuList - additionalServiceItems prop:', additionalServiceItems);
  console.log('🔍 CateringMenuList - additionalServiceItems length:', additionalServiceItems?.length);
  console.log('🔍 CateringMenuList - cateringItemsListFromRedux length:', cateringItemsListFromRedux?.length);
  console.log('🔍 CateringMenuList - cateringItemsList (selected):', cateringItemsList);
  console.log('🔍 CateringMenuList - cateringItemsList length:', cateringItemsList?.length);
    
  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const allCateringCartItems = useSelector(selectCateringCartItems); // Always call the hook
  const cartRestaurantDetails = useSelector(selectCartRestaurantDetails);
  const brandList = useSelector(selectCateringBrandsList);
  const cateringRestaurantId = useSelector(selectCateringRestaurantId);
  const defaultBrandName = brandList?.find(brand => brand.id == cateringRestaurantId)?.name_en;
  
  // Use appropriate filters based on whether this is additional service or main package
  const mainFilters = useSelector(selectCateringCartFilters);
  const additionalFilters = useSelector(selectCateringAdditionalServiceFilters);
  const filters = isAdditionalService ? additionalFilters : mainFilters;
  const [loadingItemId, setLoadingItemId] = useState(null);
  const [showAddOnModal, setShowAddOnModal] = useState(false);
  const [itemForAddOnSelection, setItemForAddOnSelection] = useState(null);
  const [showRemoveWarningModal, setShowRemoveWarningModal] = useState(false);
  const [itemForRemoveWarning, setItemForRemoveWarning] = useState(null);
  const [addOnItemsForWarning, setAddOnItemsForWarning] = useState([]);
  const [packageInfoForRemoveWarning, setPackageInfoForRemoveWarning] = useState(null);
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const cardRefs = useRef({});
  const tooltipTimeoutRef = useRef(null);
  const dispatch = useDispatch();

  // Get items from the active package if available, otherwise from all packages
  const cartItems = activePackageId
    ? (() => {
      const activePackage = cateringCartPackages.find(pkg => pkg.id === activePackageId);
      return Array.isArray(activePackage?.items) ? activePackage.items : [];
    })()
    : allCateringCartItems; // Use the hook result





  // Helper function to calculate price based on PAX and add_on_options
  const calculateItemPrice = (item, addOnType = null) => {
    const currentPax = filters.pax || 5;

    // Use the utility function which handles all cases including new add_on_type structure
    return getCateringItemPrice(item, currentPax, addOnType);
  };

  // Helper function to format price with KWD currency prefix
  const formatPrice = (price) => {
    // If price already includes KWD, return as is
    if (typeof price === "string" && price.startsWith("KWD ")) {
      return price;
    }

    // Convert to number and format with 3 decimal places, then add KWD prefix
    const numericPrice = parseFloat(price);
    return `KWD ${numericPrice.toFixed(3)}`;
  };

  // Get non-variant cart items
  const nonVariantCartItems = cartItems.filter((item) => !item.has_variants);

  // Memoized function to get item quantity from Redux cart (sum all variations including add-ons)
  const getItemQuantity = useCallback(
    (itemId) => {
      const cartItemsForId = cartItems.filter(
        (item) => item.id === itemId && !item.has_variants
      );
      // Sum quantities of all variations (normal + half + full)
      return cartItemsForId.reduce((total, item) => total + (item.quantity || 0), 0);
    },
    [cartItems]
  );

  // Handle add-on selection from modal
  const handleAddOnSelect = (addOnType) => {
    if (itemForAddOnSelection) {
      addItem(itemForAddOnSelection, addOnType);
      setItemForAddOnSelection(null);
    }
  };

  // Handle modal close
  const handleAddOnModalClose = () => {
    setShowAddOnModal(false);
    setItemForAddOnSelection(null);
  };

  // Handle remove warning modal close
  const handleRemoveWarningModalClose = () => {
    setShowRemoveWarningModal(false);
    setItemForRemoveWarning(null);
    setAddOnItemsForWarning([]);
    setPackageInfoForRemoveWarning(null);
  };

  // Warning modal to be continued - simplified for catering
  const checkExistingCartItems = () => {
    // For catering, we're more flexible with cart items
    return true;
  };

  // Helper function to calculate current total payable
  const calculateCurrentTotal = () => {
    if (!activePackageId) return 0;

    const currentPackage = cateringCartPackages.find(pkg => pkg.id === activePackageId);
    if (!currentPackage) return 0;

    const currentPax = filters.pax || 20;
    const packageBasePrice = calculateTotalPackageBasePrice([currentPackage], currentPax);
    const chargeableItemsCost = calculateTotalChargeableItemsCost([currentPackage], currentPax);

    return packageBasePrice + chargeableItemsCost;
  };

  // In modify mode, validation happens at checkout, not during item add/remove
  // This allows users to freely swap items as long as final total >= original total

  // In modify mode, category limits are flexible to allow swapping items
  // Validation focuses on maintaining minimum total, not category constraints

  // Add item to catering cart (both for new and existing items)
  const addItem = (itemToAdd, addOnType = null) => {
    // In modify order mode, allow adding - only enforce minimum total at checkout
    if (cartRestaurantDetails && cartItems.length !== 0) {
      checkExistingCartItems();
    }

    // Get the active package for category limit checks
    const activePackage = cateringCartPackages.find(pkg => pkg.id === activePackageId);

    // NEW: Skip add-on selection modal for standalone packages
    // Standalone items (price_type: "Standalone Item Based") don't have half/full options
    // They are added directly and charge their own price
    const isStandaloneItem = itemToAdd.price_type === "Standalone Item Based";
    
    console.log('🛒 Adding item to cart:', {
      itemName: itemToAdd.name_en || itemToAdd.name,
      itemId: itemToAdd.id,
      price_type: itemToAdd.price_type,
      isStandaloneItem,
      hasAddOnOptions: itemRequiresAddOnSelection(itemToAdd),
      add_on_options: itemToAdd.add_on_options,
      addOnType,
      activePackageId,
      cartItemsLength: cartItems.length
    });
    
    // Check if we need to show add-on selection modal (only for non-standalone items)
    if (!isStandaloneItem && itemRequiresAddOnSelection(itemToAdd) && !addOnType) {
      console.log('🔍 Checking if should show add-on modal...');
      
      // Determine the correct PAX to use
      // For additional services, use package-specific PAX; otherwise use filters.pax
      const currentPax = (activePackage?.isAdditionalService && activePackage?.pax) 
        ? activePackage.pax 
        : filters.pax;
      
      console.log('🔍 Using PAX for modal check:', {
        currentPax,
        isAdditionalService: activePackage?.isAdditionalService,
        packagePax: activePackage?.pax,
        filtersPax: filters.pax
      });
      
      // Check if category limit is exceeded
      const shouldShowAddOn = shouldShowAddOnOptions(itemToAdd, cartItems, activePackage, currentPax);
      console.log('🔍 shouldShowAddOn result:', shouldShowAddOn);

      if (shouldShowAddOn) {
        console.log('⚠️ Showing add-on modal - blocking item add');
        // Show add-on selection modal
        setItemForAddOnSelection(itemToAdd);
        setShowAddOnModal(true);
        return;
      }
    }
    
    // NEW: For Matrix Based items without valid add-on options, enforce category limit strictly
    // These items cannot be added as chargeable add-ons, so block when limit is reached
    if (!isStandaloneItem && itemToAdd.price_type === "Matrix Based" && !itemRequiresAddOnSelection(itemToAdd) && !addOnType) {
      console.log('🔍 Matrix Based item without valid add-on options - checking category limit...');
      console.log('🔍 Active package details:', {
        packageId: activePackage?.id,
        packageName: activePackage?.name_en,
        isAdditionalService: activePackage?.isAdditionalService,
        packagePax: activePackage?.pax,
        hasCategoriesArray: !!activePackage?.categories,
        categoriesCount: activePackage?.categories?.length
      });
      
      // Find the category limit for this item
      const categoryLimit = activePackage?.categories?.find(category => category.id === itemToAdd.category_id);
      console.log('🔍 Category limit found:', categoryLimit ? {
        categoryId: categoryLimit.id,
        categoryName: categoryLimit.name_en,
        hasChooseRule: !!categoryLimit.choose_rule,
        chooseRuleLength: categoryLimit.choose_rule?.length
      } : 'NOT FOUND');
      
      if (categoryLimit && categoryLimit.choose_rule && categoryLimit.choose_rule.length > 0) {
        // Determine the correct PAX to use
        // For additional services, use package-specific PAX; otherwise use filters.pax
        const currentPax = (activePackage?.isAdditionalService && activePackage?.pax) 
          ? activePackage.pax 
          : filters.pax;
        
        console.log('🔍 Using PAX for category limit check:', {
          currentPax,
          isAdditionalService: activePackage?.isAdditionalService,
          packagePax: activePackage?.pax,
          filtersPax: filters.pax
        });
        
        // Find the appropriate choose rule for current PAX
        // Sort rules by PAX ascending to find the lowest applicable rule
        const sortedRulesAsc = [...categoryLimit.choose_rule].sort((a, b) => parseInt(a.pax) - parseInt(b.pax));
        
        // First try to find exact match (current PAX >= rule PAX)
        const sortedRulesDesc = [...categoryLimit.choose_rule].sort((a, b) => parseInt(b.pax) - parseInt(a.pax));
        let matchingRule = sortedRulesDesc.find(rule => currentPax >= parseInt(rule.pax));
        
        // If no match found (PAX too low), use the LOWEST PAX rule as it represents minimum requirements
        if (!matchingRule && sortedRulesAsc.length > 0) {
          matchingRule = sortedRulesAsc[0];
          console.log('⚠️ No matching rule found for current PAX - using lowest PAX rule as minimum requirement');
        }
        
        console.log('🔍 Matching rule:', matchingRule ? {
          rulePax: matchingRule.pax,
          ruleChoose: matchingRule.choose,
          currentPax: currentPax
        } : 'NOT FOUND');
        
        if (matchingRule && matchingRule.choose >= 0) {
          // Count current items in this category (excluding add-on items)
          const itemsInCategory = cartItems.filter(cartItem => 
            cartItem.category_id === itemToAdd.category_id && !cartItem.add_on_type
          );
          
          // Calculate total quantity in category (non-add-on items only)
          const totalCategoryQuantity = itemsInCategory.reduce((total, cartItem) => {
            if (cartItem.has_variants && cartItem.items) {
              return total + cartItem.items.reduce((variantTotal, variantItem) => 
                variantTotal + (variantItem.quantity || 0), 0
              );
            } else {
              return total + (cartItem.quantity || 0);
            }
          }, 0);
          
          const maxFreeItems = matchingRule.choose;
          
          console.log('🔍 Category limit check:', {
            categoryId: itemToAdd.category_id,
            currentPax,
            totalCategoryQuantity,
            maxFreeItems,
            isLimitReached: totalCategoryQuantity >= maxFreeItems
          });
          
          // If category limit is reached and item cannot be added as chargeable add-on, block it
          if (totalCategoryQuantity >= maxFreeItems) {
            console.log('❌ Category limit reached and item has no valid add-on options - blocking add');
            alert(`You have reached the maximum selection limit for this category (${maxFreeItems} items). This item cannot be added as it doesn't have pricing options.`);
            return;
          }
        } else {
          console.log('⚠️ No valid matching rule - skipping category limit check');
        }
      }
    }
    
    console.log('✅ Proceeding with item add (no modal needed)');

    // Look for existing item in cart by ID and add_on_type (avoiding has_variants mismatch)
    const existingItem = cartItems.find(
      (cartItem) => cartItem.id === itemToAdd.id &&
        (cartItem.add_on_type || null) === (addOnType || null)
    );

    let updatedItem;
    if (existingItem) {
      // Update existing item (keep all properties intact, just update quantity)
      updatedItem = {
        ...existingItem,
        quantity: existingItem.quantity + 1,
      };
    } else {
      // Create new item (calculate price based on PAX and add_on_options)
      const calculatedPrice = calculateItemPrice(itemToAdd, addOnType);
      updatedItem = {
        ...itemToAdd,
        quantity: 1,
        price: formatPrice(calculatedPrice),
        add_on_type: addOnType || undefined, // Store the add-on type if provided
      };
    }

    console.log('📦 Dispatching item to Redux:', {
      activePackageId,
      itemId: updatedItem.id,
      itemName: updatedItem.name_en || updatedItem.name,
      quantity: updatedItem.quantity,
      price: updatedItem.price,
      isExisting: !!existingItem
    });
    
    // Dispatch updated item to catering cart Redux or nested package
    if (activePackageId) {
      dispatch(addItemToCateringPackage({ 
        packageId: activePackageId, 
        item: updatedItem,
        packageMeta: { isAdditionalService }
      }));
      console.log('✅ Item dispatched to Redux successfully');
    } else {
      // For non-package catering items, we need a packageId. Create a default one or skip.
      console.warn('⚠️ No activePackageId - item NOT added!');
      console.warn('setCateringCartItems now requires packageId. Consider using addItemToCateringPackage instead.');
    }

    // Calculate subtotal from the updated cart items
    const updatedCart = [
      ...cartItems.filter(
        (cartItem) => cartItem.id !== itemToAdd.id ||

          (cartItem.add_on_type || null) !== (addOnType || null)
      ),
      updatedItem,
    ];
    dispatch(setCateringCartSubtotal(updateSubtotal(updatedCart)));
  };

  // Remove item from catering cart
  const removeItem = (itemToRemove) => {
    console.log('🗑️ MenuList - Attempting to remove item:', {
      itemId: itemToRemove.id,
      itemName: itemToRemove.name_en || itemToRemove.name,
      categoryId: itemToRemove.category_id,
      add_on_type: itemToRemove.add_on_type,
      quantity: getItemQuantity(itemToRemove.id)
    });
    
    // When clicking remove from menu card, we should ONLY remove normal items
    // Add-on items should only be removed via the dropdown buttons
    
    const cartItemsForThisId = cartItems.filter(cartItem => cartItem.id === itemToRemove.id && !cartItem.has_variants);
    
    console.log('🗑️ MenuList - Cart items for this menu ID:', {
      itemId: itemToRemove.id,
      cartItemsFound: cartItemsForThisId.length,
      cartItems: cartItemsForThisId.map(ci => ({
        id: ci.id,
        name: ci.name_en || ci.name,
        add_on_type: ci.add_on_type || 'normal',
        quantity: ci.quantity
      }))
    });
    
    if (cartItemsForThisId.length === 0) {
      console.log('❌ MenuList - No cart items found for this ID');
      return;
    }
    
    // Find normal items (no add_on_type) for this item
    const normalItemsForThisItem = cartItemsForThisId.filter(ci => !ci.add_on_type);
    
    if (normalItemsForThisItem.length === 0) {
      console.log('⚠️ MenuList - No normal items found, only add-ons. Use dropdown to remove add-ons.');
      return;
    }
    
    // Check if there are add-ons in the category before removing normal item
    const addOnItemsInCategory = cartItems.filter(cartItem =>
      cartItem.category_id === itemToRemove.category_id && 
      cartItem.add_on_type && 
      (cartItem.add_on_type === 'half' || cartItem.add_on_type === 'full')
    );
    
    if (addOnItemsInCategory.length > 0) {
      console.log('⚠️ MenuList - Cannot remove normal item - add-on items exist in category. Showing warning modal.');
      const packageForWarning = activePackageId
        ? cateringCartPackages.find(pkg => pkg.id === activePackageId)
        : cateringCartPackages.find(pkg => (pkg.items || []).some(cartItem => cartItem.id === itemToRemove.id));
      const packageBrandName = packageForWarning?.restaurant_id
        ? (brandList?.find(brand => brand.id == packageForWarning.restaurant_id)?.name_en || defaultBrandName)
        : defaultBrandName;
      setPackageInfoForRemoveWarning({
        packageName: packageForWarning?.name_en || packageForWarning?.title || 'Package',
        brandName: packageBrandName || 'Catering'
      });
      setItemForRemoveWarning(normalItemsForThisItem[0]);
      setAddOnItemsForWarning(addOnItemsInCategory);
      setShowRemoveWarningModal(true);
      return; // Prevent removal
    }
    
    // Remove normal item
    const itemToActuallyRemove = normalItemsForThisItem[0];
    console.log('✅ MenuList - Removing normal item:', itemToActuallyRemove.name_en || itemToActuallyRemove.name);

    // STANDARD REMOVAL LOGIC
    if (!itemToActuallyRemove) return;

    // Decrement quantity
    const updatedItem = {
      ...itemToActuallyRemove,
      quantity: itemToActuallyRemove.quantity - 1,
    };

    if (activePackageId) {
      if (updatedItem.quantity <= 0) {
        dispatch(removeItemFromCateringPackage({
          packageId: activePackageId,
          itemId: itemToActuallyRemove.id,
          addOnType: itemToActuallyRemove.add_on_type || null
        }));
      } else {
        dispatch(addItemToCateringPackage({ 
          packageId: activePackageId, 
          item: updatedItem,
          packageMeta: { isAdditionalService }
        }));
      }
    } else {
      console.warn('setCateringCartItems now requires packageId. Consider using addItemToCateringPackage instead.');
    }

    // Calculate updated cart after removal
    const updatedCart = updatedItem.quantity <= 0
      ? cartItems.filter(
        (cartItem) => cartItem.id !== itemToActuallyRemove.id ||
          (cartItem.add_on_type || null) !== (itemToActuallyRemove.add_on_type || null)
      )
      : cartItems.map((cartItem) =>
        cartItem.id === itemToActuallyRemove.id &&
          (cartItem.add_on_type || null) === (itemToActuallyRemove.add_on_type || null)
          ? updatedItem
          : cartItem
      );

    dispatch(setCateringCartSubtotal(updateSubtotal(updatedCart)));
  };

  const handleVariantItems = (item, event) => {
    if (event === "add") {
      dispatch(setEditingVariantItem(null));

      // For catering, we don't need to fetch item details as we already have cateringItems
      // Just set the editing state directly
      setLoadingItemId(item.id); // Set loading state briefly for UI feedback

      // Find the item details from cateringItems
      const itemDetails = cateringItemsList.find(cateringItem => cateringItem.id === item.id);

      if (itemDetails) {
        // Set the editing variant item directly without API call
        dispatch(setEditingVariantItem({
          productInfo: {
            ...itemDetails,
            is_catering: true, // Flag to identify this as a catering item
            catering_item: true,
            packageId: activePackageId // Thread packageId for nested operations
          },
          variantInfo: null // Will be set when specific variant is selected
        }));

        // Show the product detail modal for variant selection
        dispatch(setShowProductDetail(true));
      }

      setLoadingItemId(null); // Reset loading state immediately
    }
  };

  const handleNonVariantItems = (item, event) => {

    dispatch(setShowProductDetail(false));

    // We need to operate on the original item from cateringItemsList
    // Not the displayItem which might have outdated quantity from previous render
    const cateringItem = cateringItemsList.find((cateringItem) => cateringItem.id === item.id);

    if (!cateringItem) return;

    if (event === "add") {
      addItem(cateringItem);
    } else if (event === "remove") {
      removeItem(cateringItem);
    }
  };

  const handleSelectedItem = (item, event) => {
    if (item.has_variants && item.variants?.length !== 0) {
      handleVariantItems(item, event);
    } else {
      handleNonVariantItems(item, event);
    }
  };

  const removeSubItem = (item, variantItem) => {
    // In modify order mode, allow removal - validation happens at checkout
    // Dispatch the variant removal action to catering cart
    if (activePackageId) {
      dispatch(removeVariantItemFromCateringPackage({ packageId: activePackageId, productId: item.id, variantId: variantItem.variantId }));
    } else {
      // For non-package catering items, we need a packageId. Create a default one or skip.
      console.warn('removeCateringCartVariantItem now requires packageId. Consider using removeVariantItemFromCateringPackage instead.');
    }
  };

  const addSubItem = (item, variantItem) => {

    // Dispatch the variant add action to catering cart
    if (activePackageId) {
      dispatch(addVariantItemToCateringPackage({ packageId: activePackageId, productId: item.id, variantId: variantItem.variantId }));
    } else {
      // For non-package catering items, we need a packageId. Create a default one or skip.
      console.warn('addCateringCartVariantItem now requires packageId. Consider using addVariantItemToCateringPackage instead.');
    }
  };

  // Add more of an add-on item (half/full)
  const addAddOnItem = (addOnItem) => {
    if (activePackageId) {
      dispatch(addItemToCateringPackage({ 
        packageId: activePackageId, 
        item: { ...addOnItem, quantity: addOnItem.quantity + 1 },
        packageMeta: { isAdditionalService }
      }));
    }
  };

  // Remove an add-on item (half/full)
  const removeAddOnItem = (addOnItem) => {
    if (activePackageId) {
      if (addOnItem.quantity <= 1) {
        dispatch(removeItemFromCateringPackage({
          packageId: activePackageId,
          itemId: addOnItem.id,
          addOnType: addOnItem.add_on_type
        }));
      } else {
        dispatch(addItemToCateringPackage({ 
          packageId: activePackageId, 
          item: { ...addOnItem, quantity: addOnItem.quantity - 1 } 
        }));
      }
    }
  };

  // Toggle complimentary count for an add-on item
  const toggleComplimentary = (addOnItem, newComplimentaryCount) => {
    if (activePackageId) {
      dispatch(addItemToCateringPackage({ 
        packageId: activePackageId, 
        item: { ...addOnItem, complimentaryCount: newComplimentaryCount } 
      }));
    }
  };

  const cardActiveHandler = (itemId, has_variants) => {

    if (has_variants) {
      const isVariantInCart = cartItems.some(
        (item) => item.id === itemId && item.has_variants
      );
      return isVariantInCart;
    } else {
      const isItemInCart = cartItems.some(
        (item) => item.id === itemId && !item.has_variants
      );
      return isItemInCart;
    }
  };

  // No need for branch-specific cart restaurant details in catering

  // Return early if no catering items
  if (!cateringItemsList) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-gray-500">No catering items available</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Add-On Selection Modal */}
      {showAddOnModal && itemForAddOnSelection && (
        <AddOnSelectionModal
          item={itemForAddOnSelection}
          onSelect={handleAddOnSelect}
          onClose={handleAddOnModalClose}
        />
      )}

      {/* Remove Item Warning Modal */}
      {showRemoveWarningModal && itemForRemoveWarning && (
        <RemoveItemWarningModal
          item={itemForRemoveWarning}
          addOnItems={addOnItemsForWarning}
          packageInfo={packageInfoForRemoveWarning}
          onClose={handleRemoveWarningModalClose}
        />
      )}

      <div className="flex-1 overflow-y-auto overflow-x-visible">
        <div className="grid grid-cols-3 gap-4 pb-8 pr-2" style={{ overflow: 'visible' }}>
          {cateringItemsList
            ?.filter(
              (item) =>
                (activeCategory === "All Menu" ||
                  item.category_id === activeCategory) &&
                (item.name_en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  item.description_en
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()))
            )
            .sort((a, b) => {
              // Only sort when All Menu is selected
              if (activeCategory === "All Menu") {
                return a.category_id - b.category_id;
              }
              return 0;
            })
            .map((item) => {
              // Get current quantity from Redux cart
              const currentQuantity = getItemQuantity(item.id);

              // Calculate the price based on PAX and add_on_options
              const calculatedPrice = calculateItemPrice(item);

              // Format the price to include KWD prefix for display
              const displayItem = {
                ...item,
                price: formatPrice(calculatedPrice),
                quantity: currentQuantity,
              };

              // Find add-on items (half/full) for this menu item
              const addOnItemsForCard = cartItems.filter(
                (cartItem) =>
                  cartItem.id === item.id && 
                  !cartItem.has_variants &&
                  cartItem.add_on_type && 
                  (cartItem.add_on_type === 'half' || cartItem.add_on_type === 'full')
              );

              return (
                <div
                  key={item.id}
                  ref={(el) => cardRefs.current[item.id] = el}
                  className={`border-2 p-2 rounded-xl shadow-md flex flex-col gap-4 relative w-full min-w-[249px] h-36 ${cardActiveHandler(item.id, item.has_variants)
                      ? "border-amber-100"
                      : "border-gray-150"
                    } relative transition-all duration-300 ease-in-out hover:shadow-lg`}
                  onMouseEnter={() => {
                    // Clear any existing timeout
                    if (tooltipTimeoutRef.current) {
                      clearTimeout(tooltipTimeoutRef.current);
                      tooltipTimeoutRef.current = null;
                    }
                    
                    setHoveredItemId(item.id);
                    // Calculate tooltip position
                    const card = cardRefs.current[item.id];
                    if (card) {
                      const rect = card.getBoundingClientRect();
                      setTooltipPosition({
                        x: rect.left + rect.width / 2,
                        y: rect.top - 10
                      });
                    }
                  }}
                  onMouseLeave={() => {
                    // Delay hiding tooltip to allow moving mouse to tooltip
                    tooltipTimeoutRef.current = setTimeout(() => {
                      if (!isTooltipHovered) {
                        setHoveredItemId(null);
                      }
                    }, 100);
                  }}
                  style={{
                    overflow: 'visible',
                    marginBottom: (() => {
                      // Find variant items for this product
                      const variantItems = cartItems.find(
                        (variantItem) =>
                          variantItem.id === item.id && variantItem?.has_variants
                      );

                      // If variant items exist, calculate dynamic margin based on number of items
                      if (variantItems) {
                        // Base margin of 50px (approx. the same as mb-20) 
                        // Plus 25px for each additional variant item beyond the first one
                        const itemsCount = variantItems.items?.length || 0;
                        return itemsCount > 0 ? 50 + (itemsCount - 1) * 25 : 0;
                      }

                      // If add-on items exist, calculate dynamic margin
                      if (addOnItemsForCard.length > 0) {
                        // Add-on dropdown has:
                        // - Header: ~25px (with border)
                        // - Each add-on item section (item + complimentary counter): ~80px
                        //   - Item row: ~45px
                        //   - Complimentary row: ~28px
                        //   - Gap: ~4px
                        // - Padding: ~20px (top + bottom py-2)
                        // - Gap between sections: ~8px per item
                        // - Extra spacing: ~15px
                        const headerHeight = 25;
                        const paddingHeight = 20;
                        const extraSpacing = 15;
                        const itemSectionHeight = 80; // Item row + complimentary counter + gap
                        const gapBetweenSections = addOnItemsForCard.length > 1 ? 8 * (addOnItemsForCard.length - 1) : 0;
                        const totalHeight = headerHeight + paddingHeight + extraSpacing + (addOnItemsForCard.length * itemSectionHeight) + gapBetweenSections;
                        return totalHeight;
                      }

                      return 0;
                    })() + 'px'
                  }}
                >
                  <MenuItemCard
                    orderedItems={cartItems}
                    item={displayItem}
                    handleSelectedItem={handleSelectedItem}
                    loadingItemId={loadingItemId}
                    disabled={disabled}
                  />
                  {cartItems.find(
                    (variantItem) =>
                      variantItem.id === item.id && variantItem.has_variants
                  ) && (
                      <VariantDropdown
                        item={cartItems.filter(
                          (variantItem) =>
                            variantItem.id === item.id && variantItem?.has_variants
                        )}
                        removeSubItem={removeSubItem}
                        addSubItem={addSubItem}
                        isCatering={true}
                      />
                    )}
                  {addOnItemsForCard.length > 0 && (
                    <AddOnDropdown
                      addOnItems={addOnItemsForCard}
                      removeAddOnItem={removeAddOnItem}
                      addAddOnItem={addAddOnItem}
                      toggleComplimentary={toggleComplimentary}
                    />
                  )}
                </div>
              );
            })}
        </div>
      </div>
      
      {/* Global Tooltip - renders outside card context via portal */}
      {hoveredItemId && (() => {
        const hoveredItem = cateringItemsList?.find(item => item.id === hoveredItemId);
        return hoveredItem ? (
          <div
            onMouseEnter={() => {
              // Clear any timeout and mark tooltip as hovered
              if (tooltipTimeoutRef.current) {
                clearTimeout(tooltipTimeoutRef.current);
                tooltipTimeoutRef.current = null;
              }
              setIsTooltipHovered(true);
            }}
            onMouseLeave={() => {
              // Hide tooltip when mouse leaves
              setIsTooltipHovered(false);
              setHoveredItemId(null);
            }}
          >
            <ItemDescriptionTooltip 
              description={hoveredItem.description_en || hoveredItem.description}
              show={true}
              position={tooltipPosition}
            />
          </div>
        ) : null;
      })()}
    </div>
  );
};

export default CateringMenuList; 